/**
 * Declarative step schema for both branches, reconstructed from the live
 * CrossCountry Mortgage flow (see ccm-form-screens/). Field keys here MUST match
 * includes/Schema.php so the REST handler maps values to Forminator fields.
 *
 * Terminal OTP ("Verify Code") from CCM is intentionally omitted — this is a
 * lead-capture form, so the final phone step submits directly (with TCPA
 * consent) instead of triggering an SMS.
 */
import type { LoanType, Step } from './types';

const YES_NO = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const PROPERTY_USAGE = [
  { value: 'primary', label: 'Primary Home', icon: 'house' },
  { value: 'secondary', label: 'Secondary Home', icon: 'building-2' },
  { value: 'rental', label: 'Rental', icon: 'key-round' },
];

const PROPERTY_TYPE = [
  { value: 'single_family', label: 'Single Family Home', icon: 'house' },
  { value: 'townhome', label: 'Townhome', icon: 'home' },
  { value: 'condominium', label: 'Condominium', icon: 'building-2' },
  { value: 'multi_family', label: 'Multi Family Home', icon: 'building' },
  { value: 'manufactured', label: 'Manufactured', icon: 'factory' },
];

const CREDIT_SCORE = [
  { value: 'excellent', label: 'Excellent 740+', icon: 'gauge' },
  { value: 'good', label: 'Good 700 - 739', icon: 'gauge' },
  { value: 'average', label: 'Average 660 - 699', icon: 'gauge' },
  { value: 'fair', label: 'Fair 600 - 659', icon: 'gauge' },
  { value: 'poor', label: 'Poor 599 or less', icon: 'gauge' },
];

const BANKRUPTCY = [
  { value: 'no', label: 'No' },
  { value: 'bankruptcy', label: 'Bankruptcy', icon: 'landmark' },
  { value: 'foreclosure', label: 'Foreclosure', icon: 'landmark' },
  { value: 'both', label: 'Both', icon: 'landmark' },
];

const YEARS = [
  { value: '1', label: '1 year' },
  { value: '2', label: '2 years' },
  { value: '3', label: '3 years' },
  { value: '4plus', label: '4+ years' },
];

const SELF_EMP_NOTE =
  'To ensure that no homeowner is left behind, we have a team that specializes in clients with self-employed income scenarios.';

/** Shared eligibility + contact steps used by both branches (contact tail differs slightly per CCM). */
function creditAndEligibility(): Step[] {
  return [
    { id: 'credit_score', key: 'credit_score', question: "What's your estimated credit score?", kind: 'choice', choices: CREDIT_SCORE },
    { id: 'military_veteran', key: 'military_veteran', question: 'Are you an active military member or veteran?', kind: 'choice', choices: YES_NO },
    { id: 'self_employed', key: 'self_employed', question: 'Are you self-employed?', kind: 'choice', choices: YES_NO, note: SELF_EMP_NOTE },
    { id: 'bankruptcy_foreclosure', key: 'bankruptcy_foreclosure', question: 'Have you filed for bankruptcy or foreclosure in the last 4 years?', kind: 'choice', choices: BANKRUPTCY },
    {
      id: 'bankruptcy_years', key: 'bankruptcy_years', question: 'How long ago was the bankruptcy?', kind: 'choice', choices: YEARS,
      when: (s) => ['bankruptcy', 'both'].includes(s.values.bankruptcy_foreclosure),
    },
    {
      id: 'foreclosure_years', key: 'foreclosure_years', question: 'How long ago was the foreclosure?', kind: 'choice', choices: YEARS,
      when: (s) => ['foreclosure', 'both'].includes(s.values.bankruptcy_foreclosure),
    },
  ];
}

const NAME_STEP: Step = {
  id: 'name', key: 'name', question: "What's your name?", kind: 'fields', continueLabel: 'Continue',
  fields: [
    { key: 'first_name', label: 'First Name', type: 'text', placeholder: 'First Name', required: true, autocomplete: 'given-name', half: true },
    { key: 'last_name', label: 'Last Name', type: 'text', placeholder: 'Last Name', required: true, autocomplete: 'family-name', half: true },
  ],
};

const LOAN_OFFICER_STEP: Step = {
  id: 'working_with_lo', key: 'working_with_lo', question: 'Are you working with a loan officer?', kind: 'loanOfficer', choices: YES_NO, skippable: true,
};

const EMAIL_STEP: Step = {
  id: 'email', key: 'email', question: "What's your email address?", kind: 'fields', continueLabel: 'Continue', note: '(We take your privacy very seriously)',
  fields: [{ key: 'email', label: 'Email', type: 'email', placeholder: 'Email', required: true, autocomplete: 'email' }],
};

/** Final step: phone + TCPA consent, submits the lead. */
const PHONE_STEP: Step = {
  id: 'phone', key: 'phone', question: "What's your phone number?", kind: 'fields', continueLabel: 'See My Options', note: '(We take your privacy very seriously)',
  fields: [{ key: 'phone', label: 'Phone Number', type: 'tel', placeholder: 'Phone Number', required: true, autocomplete: 'tel' }],
};

const PURCHASE_STEPS: Step[] = [
  { id: 'property_usage', key: 'property_usage', question: 'How will you use this home?', kind: 'choice', choices: PROPERTY_USAGE },
  { id: 'property_type', key: 'property_type', question: 'What type of home are you purchasing?', kind: 'choice', choices: PROPERTY_TYPE },
  {
    id: 'property_zip', key: 'property_zip', question: "What's the property Zip Code?", kind: 'fields', continueLabel: 'Continue',
    fields: [{ key: 'property_zip', label: 'ZipCode', type: 'text', placeholder: 'ZipCode', required: true, maxlength: 5, autocomplete: 'postal-code' }],
  },
  { id: 'first_time_buyer', key: 'first_time_buyer', question: 'Is this your first time purchasing a home?', kind: 'choice', choices: YES_NO },
  {
    id: 'buy_plan', key: 'buy_plan', question: 'Where are you in the homebuying process?', kind: 'choice',
    choices: [
      { value: 'signed_agreement', label: 'Signed a purchase agreement', icon: 'file-check' },
      { value: 'offer_pending', label: 'Offer Pending/Found Property', icon: 'clock' },
      { value: 'buying_2_6_months', label: 'Buying in 2 to 6 months', icon: 'calendar-days' },
      { value: 'researching', label: 'Researching options', icon: 'search' },
    ],
  },
  { id: 'working_with_agent', key: 'working_with_agent', question: 'Are you currently working with a real estate agent?', kind: 'choice', choices: YES_NO },
  {
    id: 'purchase_price', key: 'purchase_price', question: "What's the estimated purchase price of the home?", kind: 'fields', continueLabel: 'Continue',
    fields: [{ key: 'purchase_price', label: 'Home Value', type: 'currency', placeholder: '$', required: true }],
  },
  {
    id: 'down_payment_percent', key: 'down_payment_percent', question: 'What is your estimated down payment percent?', kind: 'fields', continueLabel: 'Continue',
    fields: [{ key: 'down_payment_percent', label: 'Down Payment', type: 'percent', placeholder: '20', required: true }],
  },
  ...creditAndEligibility(),
  NAME_STEP,
  LOAN_OFFICER_STEP,
  EMAIL_STEP,
  PHONE_STEP,
];

const REFINANCE_STEPS: Step[] = [
  {
    id: 'reason_for_refinance', key: 'reason_for_refinance', question: 'Why do you want to refinance?', kind: 'choice',
    choices: [
      { value: 'lower_payment', label: 'Lower my monthly payment', icon: 'trending-down' },
      { value: 'pay_off_faster', label: 'Pay off my mortgage faster', icon: 'rocket' },
      { value: 'cash_out', label: 'Take cash-out of home', icon: 'piggy-bank' },
      { value: 'debt_consolidation', label: 'Debt Consolidation', icon: 'layers' },
    ],
  },
  { id: 'property_usage', key: 'property_usage', question: 'How do you use this home?', kind: 'choice', choices: PROPERTY_USAGE },
  { id: 'property_type', key: 'property_type', question: 'What type of home do you have?', kind: 'choice', choices: PROPERTY_TYPE },
  {
    id: 'property_address', key: 'property_address', question: "What's the home address?", kind: 'fields', continueLabel: 'Continue', note: '(Full address needed for free property value report)',
    fields: [{ key: 'property_address', label: 'Home Address', type: 'text', placeholder: '123 Main Street, City, State, Zip', required: true, autocomplete: 'street-address' }],
  },
  {
    id: 'property_value', key: 'property_value', question: "What's the estimated value of the home?", kind: 'fields', continueLabel: 'Continue',
    fields: [{ key: 'property_value', label: 'Home Value', type: 'currency', placeholder: '$', required: true }],
  },
  {
    id: 'current_loan_balance', key: 'current_loan_balance', question: "What's the remaining balance on the mortgage?", kind: 'fields', continueLabel: 'Continue',
    fields: [{ key: 'current_loan_balance', label: 'Remaining Balance', type: 'currency', placeholder: '$', required: true }],
  },
  {
    id: 'current_interest_rate', key: 'current_interest_rate', question: "What's your current interest rate?", kind: 'fields', continueLabel: 'Continue',
    fields: [{ key: 'current_interest_rate', label: 'Interest rate', type: 'percent', placeholder: '6.5', required: true }],
  },
  {
    id: 'cash_out_amount', key: 'cash_out_amount', question: 'Would you like to borrow additional cash?', kind: 'fields', continueLabel: 'Continue',
    fields: [{ key: 'cash_out_amount', label: 'Additional Cash', type: 'currency', placeholder: '$', required: false }],
  },
  ...creditAndEligibility(),
  NAME_STEP,
  EMAIL_STEP,
  LOAN_OFFICER_STEP,
  PHONE_STEP,
];

export function stepsFor(loanType: LoanType): Step[] {
  return loanType === 'refinance' ? REFINANCE_STEPS : PURCHASE_STEPS;
}
